import { useParams, useNavigate } from "react-router-dom";
import { Clock, Package, Heart, Info, UserRound, HandHeart, ArrowRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import useActivity from "@/hooks/useActivity";
import useSeo from "@/hooks/useSeo";
import Topography from "@/components/Topography";
import { LoadingState, ErrorState, NotFoundState } from "@/components/common/States";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-brand-navy/10 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-brand-orange/12 flex items-center justify-center">
          <Icon className="w-5 h-5 text-brand-orange" />
        </div>
        <h2 className="display-title text-xl text-brand-navy">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function copyLink(path) {
  const url = `${window.location.origin}${path}`;
  const done = () => toast.success("Link copied!");
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => fallbackCopy(url, done));
    } else {
      fallbackCopy(url, done);
    }
  } catch (e) {
    fallbackCopy(url, done);
  }
}

function fallbackCopy(text, done) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    done();
  } catch (e) {
    toast.error("Could not copy link");
  }
}

function RoleCard({ testid, copyTestid, icon: Icon, title, subtitle, onOpen, copyPath, variant }) {
  const primary = variant === "primary";
  return (
    <div
      className={`rounded-2xl p-6 pib-shadow flex flex-col ${
        primary ? "bg-brand-orange text-white" : "bg-white border-2 border-brand-navy/15"
      }`}
    >
      <button
        data-testid={testid}
        onClick={onOpen}
        className="group text-left flex-1"
      >
        <Icon className={`w-8 h-8 mb-3 ${primary ? "text-white" : "text-brand-orange"}`} />
        <h3 className={`display-title text-2xl ${primary ? "text-white" : "text-brand-navy"}`}>{title}</h3>
        <p className={`mt-1 ${primary ? "text-white/90" : "text-brand-navy/70"}`}>{subtitle}</p>
        <span className={`mt-3 inline-flex items-center gap-1.5 font-semibold ${primary ? "text-white" : "text-brand-orange"}`}>
          Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </button>
      <button
        type="button"
        data-testid={copyTestid}
        onClick={() => copyLink(copyPath)}
        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full min-h-[44px] px-4 text-sm font-semibold transition-colors ${
          primary
            ? "bg-white/20 hover:bg-white/30 text-white"
            : "bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-navy"
        }`}
      >
        <Link2 className="w-4 h-4" /> Copy link
      </button>
    </div>
  );
}

export default function ActivityOverview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { activity, status } = useActivity(slug);
  useSeo(
    activity ? activity.name : "Activity",
    activity ? activity.shortDescription : "Project in a Box activity overview."
  );

  if (status === "loading") return <LoadingState />;
  if (status === "notfound") return <NotFoundState />;
  if (status === "error" || !activity) return <ErrorState onRetry={() => window.location.reload()} />;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-navy text-white">
        <Topography color="#F5F0E1" opacity={0.08} />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
              {activity.directoryCard?.categoryTag || activity.categories.join(" + ")}
            </span>
            <span className="inline-flex items-center gap-1 text-sm text-white/80">
              <Clock className="w-4 h-4" /> {activity.duration}
            </span>
          </div>
          <h1 className="display-title text-3xl sm:text-5xl">{activity.name}</h1>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-8 space-y-5">
        {/* Role choice - scoped to this activity */}
        <div>
          <h2 className="display-title text-2xl text-brand-navy">How are you joining?</h2>
          <p className="mt-1 text-brand-navy/70">Pick your role for this activity.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <RoleCard
              testid="role-card-organizer"
              copyTestid="copy-link-organizer"
              icon={UserRound}
              title="I'm an Organizer"
              subtitle="I'm setting up or running an event."
              onOpen={() => navigate(`/activities/${slug}/organizer`)}
              copyPath={`/activities/${slug}/organizer`}
              variant="primary"
            />
            <RoleCard
              testid="role-card-volunteer"
              copyTestid="copy-link-volunteer"
              icon={HandHeart}
              title="I'm a Volunteer"
              subtitle="I'm here to do the activity."
              onOpen={() => navigate(`/activities/${slug}/volunteer`)}
              copyPath={`/activities/${slug}/volunteer`}
              variant="secondary"
            />
          </div>
        </div>

        <Section icon={Info} title="About this activity">
          <p className="text-brand-navy/80 leading-relaxed">{activity.about}</p>
        </Section>

        <Section icon={Heart} title="Who it helps">
          <p className="text-brand-navy/80 leading-relaxed">{activity.impact}</p>
        </Section>

        <Section icon={Package} title="What's in the kit">
          {activity.materialsNote && <p className="text-sm text-brand-navy/60 mb-3">{activity.materialsNote}</p>}
          <ul className="grid sm:grid-cols-2 gap-2">
            {activity.materialsPerKit.map((m, i) => (
              <li key={i} className="flex items-start justify-between gap-3 rounded-xl bg-brand-cream px-3.5 py-2.5">
                <span className="text-[15px] text-brand-navy">{m.name}</span>
                <span className="flex-shrink-0 text-sm font-semibold text-brand-orange whitespace-nowrap">{m.qty}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </div>
  );
}
